/**
 * MobileMenu — Full-screen mobile navigation overlay
 * La Tienda Del Padel
 */
class MobileMenu {
  constructor() {
    this.menu = document.getElementById('mobile-menu');
    if (!this.menu) return;

    this.openBtns = document.querySelectorAll('[data-mobile-menu-open]');
    this.closeBtns = document.querySelectorAll('[data-mobile-menu-close]');
    this.accordionTriggers = this.menu.querySelectorAll('.mobile-menu__accordion-trigger');
    this.subAccordionTriggers = this.menu.querySelectorAll('.mobile-menu__sub-accordion-trigger');
    this.focusableElements = [];
    this.firstFocusable = null;
    this.lastFocusable = null;
    this.isOpen = false;

    this.bindEvents();
  }

  bindEvents() {
    // Open buttons
    this.openBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.open());
    });

    // Close buttons
    this.closeBtns.forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Accordion triggers (level 2)
    this.accordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.toggleAccordion(trigger));
    });

    // Sub-accordion triggers (level 3)
    this.subAccordionTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.toggleAccordion(trigger));
    });
  }

  open() {
    this.isOpen = true;
    this.menu.classList.add('is-open');
    this.menu.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');

    // Update hamburger aria-expanded
    this.openBtns.forEach((btn) => {
      btn.setAttribute('aria-expanded', 'true');
    });

    // Set stagger animation delays
    this.applyStaggerDelays();

    // Set up focus trap
    this.updateFocusableElements();
    if (this.closeBtns.length > 0) {
      this.closeBtns[0].focus();
    }

    // Bind focus trap
    this._trapFocusHandler = (e) => this.trapFocus(e);
    document.addEventListener('keydown', this._trapFocusHandler);
  }

  close() {
    this.isOpen = false;
    this.menu.classList.remove('is-open');
    this.menu.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');

    // Update hamburger aria-expanded
    this.openBtns.forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });

    // Close all accordions (both levels)
    [...this.accordionTriggers, ...this.subAccordionTriggers].forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const contentId = trigger.getAttribute('aria-controls');
      const content = document.getElementById(contentId);
      if (content) {
        content.classList.remove('is-expanded');
        content.setAttribute('aria-hidden', 'true');
      }
    });

    // Return focus to the open button
    if (this.openBtns.length > 0) {
      this.openBtns[0].focus();
    }

    // Remove focus trap
    if (this._trapFocusHandler) {
      document.removeEventListener('keydown', this._trapFocusHandler);
    }
  }

  toggleAccordion(trigger) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    const contentId = trigger.getAttribute('aria-controls');
    const content = document.getElementById(contentId);

    if (!content) return;

    // When opening, collapse all siblings at the same level first
    if (!isExpanded) {
      const isTopLevel = trigger.classList.contains('mobile-menu__accordion-trigger');
      const siblings = isTopLevel
        ? this.accordionTriggers
        : trigger.closest('.mobile-menu__accordion-content')
            ?.querySelectorAll('.mobile-menu__sub-accordion-trigger') || [];

      siblings.forEach((sibling) => {
        if (sibling === trigger) return;
        if (sibling.getAttribute('aria-expanded') !== 'true') return;

        const siblingContent = document.getElementById(sibling.getAttribute('aria-controls'));
        if (!siblingContent) return;

        // Collapse nested sub-accordions inside this sibling first
        if (isTopLevel) {
          siblingContent.querySelectorAll('.mobile-menu__sub-accordion-trigger[aria-expanded="true"]').forEach((nested) => {
            const nestedContent = document.getElementById(nested.getAttribute('aria-controls'));
            if (nestedContent) {
              nested.setAttribute('aria-expanded', 'false');
              nestedContent.classList.remove('is-expanded');
              nestedContent.setAttribute('aria-hidden', 'true');
            }
          });
        }

        sibling.setAttribute('aria-expanded', 'false');
        siblingContent.classList.remove('is-expanded');
        siblingContent.setAttribute('aria-hidden', 'true');
      });
    }

    if (isExpanded) {
      trigger.setAttribute('aria-expanded', 'false');
      content.classList.remove('is-expanded');
      content.setAttribute('aria-hidden', 'true');
    } else {
      trigger.setAttribute('aria-expanded', 'true');
      content.classList.add('is-expanded');
      content.setAttribute('aria-hidden', 'false');

      // Scroll the opened section into view after CSS transition
      setTimeout(() => {
        trigger.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 320);
    }

    // Update focusable elements after accordion toggle
    this.updateFocusableElements();
  }

  applyStaggerDelays() {
    const links = this.menu.querySelectorAll('.mobile-menu__link');
    links.forEach((link, index) => {
      link.style.transitionDelay = `${index * 50}ms`;
    });
  }

  updateFocusableElements() {
    this.focusableElements = Array.from(
      this.menu.querySelectorAll(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      // Only include visible elements
      return el.offsetParent !== null || el.closest('.mobile-menu__close');
    });

    if (this.focusableElements.length > 0) {
      this.firstFocusable = this.focusableElements[0];
      this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
    }
  }

  trapFocus(e) {
    if (e.key !== 'Tab' || !this.isOpen) return;

    // Re-calculate in case accordion changed
    this.updateFocusableElements();

    if (this.focusableElements.length === 0) return;

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === this.firstFocusable) {
        e.preventDefault();
        this.lastFocusable.focus();
      }
    } else {
      // Tab
      if (document.activeElement === this.lastFocusable) {
        e.preventDefault();
        this.firstFocusable.focus();
      }
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MobileMenu();
});
